import { createSlice, createSelector } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";
import config from "../../config.json";
import { createRequestState, requestFailed, requestStarted, requestSucceeded } from "./requestState";

const slice = createSlice({
  name: "dashboard",
  initialState: {
    list: [],
    loading: false,
    lastFetch: null,
    wfPhases: [],
    reportData: null,
    reportByMonth: null,
    reportByMonth1: null,
    allAppTasks: [],
    allAppTasksByMonth: [],
    configReport: null,
    requests: {
      wfPhases: createRequestState(),
      report: createRequestState(),
      reportByMonth: createRequestState(),
      reportByMonth1: createRequestState(),
      allAppTasks: createRequestState(),
      allAppTasksByMonth: createRequestState(),
      config: createRequestState(),
    },
  },
  reducers: {
    dashboardRequestStarted: (dashboard, action) => {
      requestStarted(dashboard.requests, action.payload);
    },
    dashboardRequestFailed: (dashboard, action) => {
      requestFailed(dashboard.requests, action.payload?.key, action.payload?.error || action.payload);
    },
    wfPhasesReceived: (dashboard, action) => {
      dashboard.wfPhases = action.payload;
      requestSucceeded(dashboard.requests, "wfPhases");
    },
    configReportReceived: (dashboard, action) => {
      dashboard.configReport = action.payload;
      requestSucceeded(dashboard.requests, "config");
    },
    allAppTasksReceived: (dashboard, action) => {
      dashboard.allAppTasks = action.payload;
      requestSucceeded(dashboard.requests, "allAppTasks");
    },
    allAppTasksByMonthReceived: (dashboard, action) => {
      dashboard.allAppTasksByMonth = action.payload;
      requestSucceeded(dashboard.requests, "allAppTasksByMonth");
    },
    dashboardReportDataReceived: (dashboard, action) => {
      dashboard.reportData = action.payload;
      requestSucceeded(dashboard.requests, "report");
    },
    dashboardReportByMonthReceived: (dashboard, action) => {
      dashboard.reportByMonth = action.payload;
      requestSucceeded(dashboard.requests, "reportByMonth");
    },
    dashboardReportByMonth1Received: (dashboard, action) => {
      dashboard.reportByMonth1 = action.payload;
      requestSucceeded(dashboard.requests, "reportByMonth1");
    },
  },
});

export const {
  dashboardRequestStarted,
  dashboardRequestFailed,
  wfPhasesReceived,
  allAppTasksReceived,
  allAppTasksByMonthReceived,
  dashboardReportDataReceived,
  dashboardReportByMonthReceived,
  dashboardReportByMonth1Received,
  configReportReceived,
} = slice.actions;
export default slice.reducer;

export const loadWfPhases = (name) =>
  apiCallBegan({
    url: config.GetCodemanager + name,
    onStart: { type: dashboardRequestStarted.type, payload: "wfPhases" },
    onSuccess: wfPhasesReceived.type,
    onError: { type: dashboardRequestFailed.type, payload: { key: "wfPhases" } },
    cache: true,
    cacheDuration: 300000,
  });
export const loadDashboardReportData = (forceRefresh = false) =>
  apiCallBegan({
    url: config.GetDashboardData,
    onStart: { type: dashboardRequestStarted.type, payload: "report" },
    onSuccess: dashboardReportDataReceived.type,
    onError: { type: dashboardRequestFailed.type, payload: { key: "report" } },
    cache: true,
    cacheDuration: 30000,
    forceRefresh,
  });

export const loadDashboardReportByMonth = (year, forceRefresh = false) =>
  apiCallBegan({
    url: config.GetReceivedApplicationByMonth + year,
    onStart: { type: dashboardRequestStarted.type, payload: "reportByMonth" },
    onSuccess: dashboardReportByMonthReceived.type,
    onError: { type: dashboardRequestFailed.type, payload: { key: "reportByMonth" } },
    cache: true,
    cacheDuration: 60000,
    forceRefresh,
  });

export const loadDashboardReportByMonth1 = (year, forceRefresh = false) =>
  apiCallBegan({
    url: config.GetReceivedApplicationByMonth + year,
    onStart: { type: dashboardRequestStarted.type, payload: "reportByMonth1" },
    onSuccess: dashboardReportByMonth1Received.type,
    onError: { type: dashboardRequestFailed.type, payload: { key: "reportByMonth1" } },
    cache: true,
    cacheDuration: 60000,
    forceRefresh,
  });

export const loadAllAppTasks = (year, forceRefresh = false) =>
  apiCallBegan({
    url: config.GetAllWfTaskStatus + year,
    onStart: { type: dashboardRequestStarted.type, payload: "allAppTasks" },
    onSuccess: allAppTasksReceived.type,
    onError: { type: dashboardRequestFailed.type, payload: { key: "allAppTasks" } },
    cache: true,
    cacheDuration: 60000,
    forceRefresh,
  });
export const loadAllAppTasksByMonth = (year, forceRefresh = false) =>
  apiCallBegan({
    url: config.GetAllWfTaskStatusByMonth + year,
    onStart: { type: dashboardRequestStarted.type, payload: "allAppTasksByMonth" },
    onSuccess: allAppTasksByMonthReceived.type,
    onError: { type: dashboardRequestFailed.type, payload: { key: "allAppTasksByMonth" } },
    cache: true,
    cacheDuration: 60000,
    forceRefresh,
  });

export const loadConfigReport = () =>
  apiCallBegan({
    url: config.GetConfigurationReport,
    onStart: { type: dashboardRequestStarted.type, payload: "config" },
    onSuccess: configReportReceived.type,
    onError: { type: dashboardRequestFailed.type, payload: { key: "config" } },
    cache: true,
    cacheDuration: 300000,
  });

export const getWfPhases = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.wfPhases
);

export const getDashboardReportData = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.reportData
);

export const getDashboardReportByMonth = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.reportByMonth
);

export const getDashboardReportByMonth1 = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.reportByMonth1
);

export const getAllAppTasks = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.allAppTasks
);
export const getAllAppTasksByMonth = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.allAppTasksByMonth
);

export const getConfigurationReport = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.configReport
);

export const getDashboardRequests = createSelector(
  (state) => state.entities.dashboard,
  (dashboard) => dashboard.requests
);
